import { api, ApiError } from "../api";
import { iconoLapiz, iconoTacho, iconoMas } from "../iconos";
import type { CategoriaConConteo, Producto } from "../../lib/types";

type ProductoFila = Producto & { categoria: string; area: string };

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

export async function renderCategorias(contenedor: HTMLElement): Promise<void> {
  let cats: CategoriaConConteo[] = [];
  let verProductosDe: number | null = null;

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Categorias</h1>
      <p class="subtitulo">Crea, codifica (prefijo) y reasigna productos de cada categoria.</p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nueva categoria
      </summary>
      <form id="f" class="mt-4 flex flex-wrap items-end gap-3">
        <label class="flex-1 min-w-[12rem]"><span class="etiqueta">Nombre</span>
          <input id="nombre" class="campo" placeholder="Ej: Tortas" /></label>
        <label><span class="etiqueta">Prefijo (opcional)</span>
          <input id="prefijo" type="number" min="1" class="campo w-32" placeholder="13" /></label>
        <button class="btn-primario">${iconoMas} Agregar</button>
        <span id="err" class="self-center text-sm text-red-600"></span>
      </form>
    </details>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr>
          <th>Nombre</th><th class="text-right">Prefijo</th><th class="text-right">Productos</th><th>IVA</th><th class="text-right">Acciones</th>
        </tr></thead>
        <tbody id="tbody"></tbody>
      </table>
    </div>

    <div id="panelProd" class="mt-4"></div>
    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const panelProd = contenedor.querySelector("#panelProd") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;

  function opcCats(sel?: number): string {
    return cats.map((c) => `<option value="${c.id}"${c.id === sel ? " selected" : ""}>${esc(c.nombre)}</option>`).join("");
  }

  function pintar(): void {
    tbody.innerHTML = cats
      .map((c) => {
        const mixto = c.productos > 0 && c.iva_min !== c.iva_max;
        const tasa = mixto ? "" : String(c.iva_min ?? 0);
        return `<tr>
          <td class="font-medium">${esc(c.nombre)}</td>
          <td class="text-right">${c.prefijo ?? "—"}</td>
          <td class="text-right">
            <button class="chip-accion" data-ver="${c.id}" title="Ver / reasignar productos">${c.productos}</button>
          </td>
          <td>
            <span class="flex items-center gap-1">
              <select class="campo w-24 py-1 text-sm" data-iva-sel="${c.id}" ${c.productos === 0 ? "disabled" : ""}>
                ${mixto ? `<option value="" selected>mixto</option>` : ""}
                ${[0, 10, 22].map((t) => `<option value="${t}"${String(t) === tasa ? " selected" : ""}>${t}%</option>`).join("")}
              </select>
              <button class="btn-sec px-2 py-1 text-xs" data-iva-aplicar="${c.id}" ${c.productos === 0 ? "disabled" : ""}>Aplicar</button>
            </span>
          </td>
          <td class="text-right">
            <button class="chip-accion" data-editar="${c.id}" title="Editar">${iconoLapiz}</button>
            <button class="chip-accion" data-borrar="${c.id}" title="Borrar">${iconoTacho}</button>
          </td>
        </tr>`;
      })
      .join("");
    if (verProductosDe !== null) void pintarProductos(verProductosDe);
    else panelProd.innerHTML = "";
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="4" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    cats = await api.get<CategoriaConConteo[]>("/api/categorias");
    pintar();
  }

  async function pintarProductos(catId: number): Promise<void> {
    const cat = cats.find((c) => c.id === catId);
    panelProd.innerHTML = `<div class="tarjeta text-masa-400">Cargando productos...</div>`;
    const prods = await api.get<ProductoFila[]>(`/api/productos?categoria=${catId}&activo=1`);
    panelProd.innerHTML = `
      <div class="tarjeta">
        <div class="mb-2 flex items-center justify-between">
          <h2 class="text-sm font-bold uppercase tracking-wide text-masa-600">Productos de "${esc(cat?.nombre ?? "")}" (${prods.length})</h2>
          <button id="cerrarProd" class="chip-accion" title="Cerrar">✕</button>
        </div>
        ${prods.length === 0 ? `<p class="text-sm text-masa-400">Sin productos.</p>` : `
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
              <th class="text-left font-normal">Codigo</th><th class="text-left font-normal">Descripcion</th><th class="text-left font-normal">Mover a</th>
            </tr></thead>
            <tbody>${prods
              .map(
                (p) => `<tr class="border-t border-crema-100">
                  <td class="py-1 pr-2">${esc(p.codigo)}</td>
                  <td class="py-1 pr-2">${esc(p.descripcion)}</td>
                  <td class="py-1"><select class="campo py-1 text-sm" data-mover="${p.id}">${opcCats(catId)}</select></td>
                </tr>`,
              )
              .join("")}</tbody>
          </table>
        </div>`}
      </div>`;
    panelProd.querySelector("#cerrarProd")?.addEventListener("click", () => {
      verProductosDe = null;
      panelProd.innerHTML = "";
    });
    panelProd.querySelectorAll<HTMLSelectElement>("[data-mover]").forEach((sel) => {
      sel.addEventListener("change", async () => {
        const pid = Number(sel.dataset.mover);
        const nuevaCat = Number(sel.value);
        const prod = prods.find((p) => p.id === pid);
        if (!prod || nuevaCat === catId) return;
        try {
          await api.put(`/api/productos/${pid}`, {
            descripcion: prod.descripcion, categoria_id: nuevaCat, area_id: prod.area_id,
            empaque: prod.empaque, iva_tasa: prod.iva_tasa, activo: 1,
          });
          await cargar(); // actualiza conteos y el panel
        } catch (e) {
          alert("No se pudo mover: " + (e instanceof Error ? e.message : "error"));
        }
      });
    });
  }

  // Alta
  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    err.textContent = "";
    const nombre = (contenedor.querySelector("#nombre") as HTMLInputElement).value.trim();
    const prefStr = (contenedor.querySelector("#prefijo") as HTMLInputElement).value;
    if (!nombre) { err.textContent = "Falta el nombre"; return; }
    try {
      await api.post("/api/categorias", { nombre, prefijo: prefStr ? Number(prefStr) : null });
      form.reset();
      await cargar();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  // Acciones tabla
  tbody.addEventListener("click", async (ev) => {
    const target = ev.target as HTMLElement;
    const iva = target.closest("[data-iva-aplicar]");
    if (iva) {
      const id = Number(iva.getAttribute("data-iva-aplicar"));
      const c = cats.find((x) => x.id === id);
      const sel = tbody.querySelector(`[data-iva-sel="${id}"]`) as HTMLSelectElement | null;
      if (!c || !sel || sel.value === "") return;
      const tasa = Number(sel.value);
      if (!confirm(`Aplicar ${tasa}% de IVA a los ${c.productos} producto(s) de "${c.nombre}"?`)) return;
      try {
        await api.post(`/api/categorias/${id}/iva`, { iva_tasa: tasa });
        await cargar();
      } catch (e) {
        alert("No se pudo aplicar: " + (e instanceof Error ? e.message : "error"));
      }
      return;
    }
    const ver = target.closest("[data-ver]");
    if (ver) {
      const id = Number(ver.getAttribute("data-ver"));
      verProductosDe = verProductosDe === id ? null : id;
      if (verProductosDe === null) panelProd.innerHTML = "";
      else await pintarProductos(id);
      return;
    }
    const edit = target.closest("[data-editar]");
    if (edit) {
      const c = cats.find((x) => x.id === Number(edit.getAttribute("data-editar")));
      if (c) abrirEdicion(c);
      return;
    }
    const del = target.closest("[data-borrar]");
    if (del) {
      const c = cats.find((x) => x.id === Number(del.getAttribute("data-borrar")));
      if (!c) return;
      if (!confirm(`Borrar la categoria "${c.nombre}"?`)) return;
      try {
        await api.del(`/api/categorias/${c.id}`);
        if (verProductosDe === c.id) verProductosDe = null;
        await cargar();
      } catch (e) {
        alert(e instanceof ApiError ? e.message : "No se pudo borrar");
      }
    }
  });

  function abrirEdicion(c: CategoriaConConteo): void {
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-4 text-lg font-bold">Editar categoria</h2>
        <div class="space-y-3">
          <label class="block"><span class="etiqueta">Nombre</span>
            <input id="e_nom" class="campo" value="${esc(c.nombre)}" /></label>
          <label class="block"><span class="etiqueta">Prefijo</span>
            <input id="e_pref" type="number" min="1" class="campo" value="${c.prefijo ?? ""}" placeholder="sin prefijo" /></label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const nom = (dlg.querySelector("#e_nom") as HTMLInputElement).value.trim();
      const pref = (dlg.querySelector("#e_pref") as HTMLInputElement).value;
      try {
        await api.put(`/api/categorias/${c.id}`, { nombre: nom, prefijo: pref ? Number(pref) : null });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  await cargar();
}
